<script setup lang="ts">
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  Title,
  Tooltip
} from "chart.js";
import {computed, PropType} from "vue";
import {workerTypeTraits} from "@/pages/Insights/ReportsDetailPage/reportTypes";
import {useI18n} from "vue-i18n";
import {Pie} from "vue-chartjs";

interface ChartData {
  label?: string;
  labels?: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
  }[];
}

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement);
const {t} = useI18n();

const props = defineProps(
  {
    chartData: {
      type: Object as PropType<workerTypeTraits>,
      required: true,
    },
  }
);

const orderedChartData = computed(() => {
  const order = ["R", "I", "A", "S", "E", "C"];

  const sortCodes = (codes: workerTypeTraits) => order.map((code) => ({[code]: codes[code]}))
    .reduce((acc, curr) => ({...acc, ...curr}), {});

  return sortCodes(props.chartData);
});

const pieChart = computed(
  (): ChartData => {
    const label = t("components.ReportsDetailPage.worker_type_distribution.card_titles.primary_worker_type");
    if (!orderedChartData.value) return {
      label,
      datasets: []
    };
    return createPieChartData(orderedChartData.value, label);
  }
);

/**
 * Creates the data for the pie chart.
 * @param subjectCounts The subject counts to create the chart data from.
 * @param label The label for the chart.
 */
const createPieChartData = (subjectCounts: unknown, label: string): ChartData => {
  if (!subjectCounts) return {
    labels: [],
    datasets: []
  };

  const readableKeys = Object.keys(subjectCounts).map((key) => t(`assessments.holland_code.${key}`));

  return {
    labels: readableKeys,
    datasets: [
      {
        label,
        data: Object.values(subjectCounts),
        backgroundColor: [
          "#fca379",
          "#6ba8ff",
          "#7ed891",
          "#ff9d9d",
          "#8984ea",
          "#ff7a68",
        ]
      },
    ],
  };
};

const chartOptions: ChartOptions<"pie"> = {
  responsive: true,
  plugins: {
    legend: {
      position: "bottom"
    }
  }
};

</script>

<template>
  <Pie v-if="pieChart" :data="pieChart" :options="chartOptions"/>
</template>

